// @file Vuex Store for handling comment
import { Comment, JsonAPIResponse } from '@padlet/arvo'
import { captureFetchException } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import { ACCESS_WITHDRAWN, SAVE_CHANGES_FAILED } from 'bits/snackbar_helper'
import PadletApi from 'surface/padlet_api'
import { Cid, CommentId, Id } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'
import { reconcileCommentsAfterFetch } from 'vuexstore/helpers/comment'
import { RootState } from 'vuexstore/surface/types'

interface CommentState {
  isIntialCommentsLoadDone: boolean
  commentEntities: { [id: number]: Comment }
  newCommentBodyByPostCid: { [postCid: string]: string }
  pendingCommentIds: CommentId[]
  commentBeingEditedId: CommentId | null
  actionMenuCommentId: CommentId | null
  activeNewCommentPostId: Id | null
  editedCommentDraftBodyById: Record<Id, string>
  // the latest timestamp where user actively types in the inline comment input (as opposed to the comment modal)
  inlineCommentInputLastTypedInMs: number
}

const defaultState = (): CommentState => ({
  isIntialCommentsLoadDone: false,
  commentEntities: {},
  newCommentBodyByPostCid: {},
  pendingCommentIds: [],
  commentBeingEditedId: null,
  actionMenuCommentId: null,
  activeNewCommentPostId: null,
  editedCommentDraftBodyById: {},
  inlineCommentInputLastTypedInMs: 0,
})

const CommentModule: Module<CommentState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    // getters that return comment data should return nthing until the initial paginated data load is done
    commentIds: (state, getters): string[] => {
      if (!getters.isIntialCommentsLoadDone) {
        return []
      }
      return Object.keys(state.commentEntities)
    },
    commentIdsByPost: (state, getters): { [wishId: number]: CommentId[] } => {
      const commentIdsByPost = {}
      getters.commentIds.forEach((rid) => {
        const r = state.commentEntities[rid]
        commentIdsByPost[r.wish_id] = commentIdsByPost[r.wish_id] || []
        commentIdsByPost[r.wish_id].push(rid)
      })
      return commentIdsByPost
    },
    commentsByPost: (state, getters): { [wishId: number]: Comment[] } => {
      const commentsByPost = {}
      getters.commentIds.forEach((rid) => {
        const r = state.commentEntities[rid]
        commentsByPost[r.wish_id] = commentsByPost[r.wish_id] || []
        commentsByPost[r.wish_id].push(r)
      })
      return commentsByPost
    },
    isCommenting(state): boolean {
      return !!state.commentBeingEditedId || state.activeNewCommentPostId !== null
    },
    inlineCommentInputLastTypedInMs(state): number {
      return state.inlineCommentInputLastTypedInMs
    },
    newCommentBodyForPostCid(state): (postCid: Cid) => string {
      return (postCid: Cid): string => state.newCommentBodyByPostCid[postCid]
    },
    isIntialCommentsLoadDone: (state): boolean => state.isIntialCommentsLoadDone,
    editedDraftCommentBodyFromId:
      (state): Function =>
      (commentId: Id): string =>
        state.editedCommentDraftBodyById[commentId] || '',
  },
  mutations: {
    FETCH_COMMENTS_SUCCESS(state, newCommentEntities): void {
      state.commentEntities = newCommentEntities || {}
    },
    FETCH_INITIAL_PAGINATED_COMMENTS_DONE(state): void {
      state.isIntialCommentsLoadDone = true
      state.pendingCommentIds = []
    },
    UPDATE_COMMENT(state, { id, obj }): void {
      if (id < 0 || !obj) {
        return
      }
      const updatedEntity = {
        ...state.commentEntities[id],
        ...obj,
      }
      state.commentEntities = {
        ...state.commentEntities,
        [id]: updatedEntity,
      }
    },
    ADD_PENDING_COMMENT(state, pendingComment): void {
      state.commentEntities = {
        ...state.commentEntities,
        [pendingComment.id]: pendingComment,
      }
      state.pendingCommentIds.push(pendingComment.id)
    },
    REMOVE_PENDING_COMMENT(state, { commentId }): void {
      const isCommentPending = state.pendingCommentIds.includes(commentId)
      if (!isCommentPending) return
      state.pendingCommentIds = state.pendingCommentIds.filter((id) => id !== commentId)
      Vue.delete(state.commentEntities, commentId)
    },
    REVERT_PENDING_COMMENT(state, { commentId, postCid }): void {
      const isCommentPending = state.pendingCommentIds.includes(commentId)
      if (!isCommentPending) return
      state.pendingCommentIds = state.pendingCommentIds.filter((id) => id !== commentId)
      const comment = state.commentEntities[commentId]
      Vue.delete(state.commentEntities, commentId)
      Vue.set(state.newCommentBodyByPostCid, postCid, comment.body)
    },
    ADD_COMMENT(state, { comment, tempId }): void {
      // Remove old comment if present
      if (state.commentEntities[tempId]) Vue.delete(state.commentEntities, tempId)
      if (state.pendingCommentIds.includes(tempId)) {
        state.pendingCommentIds = state.pendingCommentIds.filter((id) => id !== tempId)
      }
      // Add updated comment
      state.commentEntities = { ...state.commentEntities, [comment.id]: comment }
    },
    REMOVE_COMMENT(state, id): void {
      const newCommentEntities = { ...state.commentEntities }
      delete newCommentEntities[id]
      state.commentEntities = newCommentEntities
    },
    EDIT_COMMENT_REMOTE(state, comment): void {
      const originalComment = state.commentEntities[comment.id]
      // real time can be out of sync sometimes.
      // here we make sure we are not updating a new copy with a stale one
      if (originalComment && originalComment.updated_at <= comment.updated_at) {
        state.commentEntities = {
          ...state.commentEntities,
          [comment.id]: comment,
        }
      }
    },
    START_EDITING_COMMENT(state, commentId): void {
      state.commentBeingEditedId = commentId
    },
    STOP_EDITING_COMMENT(state): void {
      state.commentBeingEditedId = null
    },
    SHOW_COMMENT_ACTION_MENU(state, commentId): void {
      if (state.actionMenuCommentId) {
        state.actionMenuCommentId = null
      } else {
        state.actionMenuCommentId = commentId
      }
    },
    HIDE_COMMENT_ACTION_MENU(state): void {
      state.actionMenuCommentId = null
    },
    START_WRITING_NEW_COMMENT(state, postId): void {
      state.activeNewCommentPostId = postId
    },
    STOP_WRITING_NEW_COMMENT(state, postId): void {
      if (state.activeNewCommentPostId === postId) {
        state.activeNewCommentPostId = null
      }
    },
    UPDATE_NEW_COMMENT_BODY(state, { postCid, body }): void {
      Vue.set(state.newCommentBodyByPostCid, postCid, body)
    },
    UPDATE_EDITED_DRAFT_COMMENT_BODY(state, { commentId, body }: { commentId: Id; body: string }): void {
      Vue.set(state.editedCommentDraftBodyById, commentId, body)
    },
    UPDATE_INLINE_COMMENT_INPUT_LAST_TYPED_IN_MS(state, newTimestamp): void {
      state.inlineCommentInputLastTypedInMs = newTimestamp
    },
  },
  actions: {
    /* ------------
     * CRUD
     * ------------ */
    // shouldResetState is a flag to reset the stored comment data
    // currently it is only set to true for the initial comment load when a Padlet page is first loaded
    fetchComments({ dispatch, rootGetters }, { shouldResetState }): void {
      PadletApi.Comment.fetch({ wallId: rootGetters.wallId })
        .then((fetchedData: JsonAPIResponse<Comment>) => {
          const { data, meta } = fetchedData
          const nextPageCursor = meta?.next
          dispatch('fetchCommentsSuccess', {
            newComments: data,
            isFirstPage: true,
            isLastPage: !nextPageCursor,
            shouldResetState,
          })

          if (nextPageCursor) {
            dispatch('fetchCommentsForPage', { pageStart: nextPageCursor })
          }
        })
        .catch((e) => {
          // Don't capture exception when it's 401, show a snackbar instead
          if (e.status === 401) {
            dispatch('snackbars/setSnackbar', ACCESS_WITHDRAWN, { root: true })
          } else {
            captureFetchException(e, { source: 'fetchComments' })
          }
        })
    },
    fetchCommentsForPage({ dispatch, rootGetters }, { pageStart }): void {
      PadletApi.Comment.fetch({ wallId: rootGetters.wallId, pageStart })
        .then((commentPage: JsonAPIResponse<Comment>) => {
          const { data, meta } = commentPage
          const nextPageCursor = meta?.next
          dispatch('fetchCommentsSuccess', {
            newComments: data,
            isFirstPage: false,
            isLastPage: !nextPageCursor,
            shouldResetState: false,
          })
          if (nextPageCursor) {
            dispatch('fetchCommentsForPage', { pageStart: nextPageCursor })
          }
        })
        .catch((e) => {
          captureFetchException(e, { source: 'fetchCommentsForPage' })
        })
    },
    fetchCommentsSuccess(context, { newComments, isFirstPage, isLastPage, shouldResetState }): void {
      const newCommentEntities = reconcileCommentsAfterFetch({
        context,
        newComments,
        isFirstPage,
        isLastPage,
        shouldResetState,
      })
      const { commit } = context
      commit('FETCH_COMMENTS_SUCCESS', newCommentEntities)
      if (isLastPage) {
        commit('FETCH_INITIAL_PAGINATED_COMMENTS_DONE')
      }
    },
    async createComment({ commit, rootState, dispatch, rootGetters }, { postId, body, createdAt }): Promise<void> {
      // tempId will be used to identify the comment in the frontend until it is persisted,
      // after which, we will use the server-provided Id.
      const tempId = 'new_' + new Date().valueOf()
      const newComment = {
        id: tempId,
        wish_id: postId,
        body: body,
        created_at: createdAt,
        user: rootState.user,
        user_id: rootState.user.id,
      }
      // not handling pending comment for now, request is quick enough
      commit('ADD_PENDING_COMMENT', newComment)

      try {
        const comment = (await PadletApi.Comment.create(newComment)).data.attributes
        commit('ADD_COMMENT', { comment, tempId })
      } catch {
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
        const post = rootGetters['post/getPostByServerId'](postId)
        if (!post || !post.cid) {
          commit('REMOVE_PENDING_COMMENT', { commentId: tempId })
        } else {
          commit('REVERT_PENDING_COMMENT', { commentId: tempId, postCid: post.cid })
        }
      } finally {
        dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
        isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
      }
    },
    deleteComment({ commit, state, dispatch }, { commentId }): void {
      const existingComment = state.commentEntities[commentId]
      commit('STOP_EDITING_COMMENT')
      if (existingComment) {
        commit('REMOVE_COMMENT', existingComment.id)
        PadletApi.Comment.delete(existingComment)
      }
      isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    startEditingComment({ commit, dispatch, state }, { commentId }): void {
      commit('START_EDITING_COMMENT', commentId)
      dispatch('updateEditedDraftCommentBody', { commentId, body: state.commentEntities[commentId].body })
    },
    stopEditingComment({ commit }): void {
      commit('STOP_EDITING_COMMENT')
    },
    async editComment({ commit, dispatch }, { id, body }): Promise<void> {
      try {
        commit('UPDATE_COMMENT', { id: id, obj: { body, html_body: '' } })
        commit('STOP_EDITING_COMMENT')
        const {
          data: {
            attributes: { html_body },
          },
        } = await PadletApi.Comment.update({ id, body })
        commit('UPDATE_COMMENT', { id: id, obj: { html_body } })
      } catch {
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
        dispatch('startEditingComment', { commentId: id })
      } finally {
        dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
        isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
      }
    },
    startWritingNewComment({ commit }, { postId }): void {
      commit('START_WRITING_NEW_COMMENT', postId)
    },
    stopWritingNewComment({ commit }, { postId }): void {
      commit('STOP_WRITING_NEW_COMMENT', postId)
    },
    updateNewCommentBody({ commit }, { postCid, body }): void {
      commit('UPDATE_NEW_COMMENT_BODY', { postCid, body })
    },
    updateEditedDraftCommentBody({ commit }, { commentId, body }: { commentId: Id; body: string }): void {
      commit('UPDATE_EDITED_DRAFT_COMMENT_BODY', { commentId, body })
    },

    /* ------------
     * REMOTE
     * ------------ */
    newCommentRemote({ commit, dispatch }, comment): void {
      commit('ADD_COMMENT', { comment })
      isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    editCommentRemote({ commit, dispatch }, payload): void {
      commit('EDIT_COMMENT_REMOTE', payload)
      isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
    },
    deleteCommentRemote({ commit, state, dispatch }, comment): void {
      if (state.commentBeingEditedId && state.commentBeingEditedId === comment.id) {
        commit('STOP_EDITING_COMMENT')
      }
      if (state.actionMenuCommentId && state.actionMenuCommentId === comment.id) {
        commit('HIDE_COMMENT_ACTION_MENU')
      }
      commit('REMOVE_COMMENT', comment.id)
      isAppUsing('expandedPostComments') && dispatch('nativeApp/postSurfaceState', null, { root: true })
    },

    /* ------------
     * ACTION MENU
     * ------------ */
    showCommentActionMenu({ commit }, { commentId }): void {
      commit('SHOW_COMMENT_ACTION_MENU', commentId)
    },
    hideCommentActionMenu({ commit }): void {
      commit('HIDE_COMMENT_ACTION_MENU')
    },

    /* ------------
     * Update the timestamp where user last actively type in the inline comment input
     * ------------ */
    updateInlineCommentInputLastTypedInMs({ commit }): void {
      commit('UPDATE_INLINE_COMMENT_INPUT_LAST_TYPED_IN_MS', Date.now())
    },
  },
}

export default CommentModule
export { CommentState }
